import { TimeZone } from '@dokucoach/common-types';
import { DateTime, Zone } from 'luxon';

export class DateUtils {
  /**
   * Checks if given date is today
   * @param firstDate first date for comparison
   * @param secondDate second date for comparison
   * @param zone? (optional) timezone of the date
   */
  public static isSameDay(
    firstDate: Date | DateTime | string | number,
    secondDate: Date | DateTime | string | number,
    zone = TimeZone.EUROPE_BERLIN,
  ): boolean {
    const first = DateUtils.toDateTime(firstDate, zone);
    const second = DateUtils.toDateTime(secondDate, zone);
    return first.day === second.day && first.month === second.month && first.year === second.year;
  }

  /**
   * Checks if given date is today
   * @param date the date to be checked
   * @param zone? (optional) timezone of the date
   */
  public static isToday(
    date: Date | DateTime | string | number,
    zone: string | Zone = TimeZone.EUROPE_BERLIN,
  ): boolean {
    return DateUtils.isSameDay(DateTime.now().setZone(zone), date);
  }

  public static isAfter(
    start: Date | DateTime | string | number,
    end: Date | DateTime | string | number,
    zone = TimeZone.EUROPE_BERLIN,
  ): boolean {
    return DateUtils.toDateTime(start, zone) > DateUtils.toDateTime(end, zone);
  }

  public static isBefore(
    start: Date | DateTime | string | number,
    end: Date | DateTime | string | number,
    zone = TimeZone.EUROPE_BERLIN,
  ): boolean {
    return DateUtils.toDateTime(start, zone) < DateUtils.toDateTime(end, zone);
  }

  public static startOfDay(date: Date | DateTime | string | number) {
    return DateUtils.toDateTime(date).startOf('day');
  }

  public static endOfDay(date: Date | DateTime | string | number) {
    return DateUtils.toDateTime(date).endOf('day');
  }

  public static startOfMonth(date: Date | DateTime | string | number) {
    return DateUtils.toDateTime(date).startOf('month');
  }

  public static endOfMonth(date: Date | DateTime | string | number) {
    return DateUtils.toDateTime(date).endOf('month');
  }

  public static startOfYear(date: Date | DateTime | string | number) {
    return DateUtils.toDateTime(date).startOf('year');
  }

  public static endOfYear(date: Date | DateTime | string | number) {
    return DateUtils.toDateTime(date).endOf('year');
  }

  /**
   * Checks if given date lies between given start and end date period
   * @param date the date to be checked
   * @param start the start of the period
   * @param end the end of the period
   *
   *
   * DateUtils.startOfDay(end)
   */
  public static isBetween(
    date: Date | DateTime | string | number,
    start: Date | DateTime | string | number,
    end: Date | DateTime | string | number,
  ) {
    return DateUtils.isAfter(date, start) && DateUtils.isBefore(date, end);
  }

  /**
   * Converts given date into a DateTime object. May throw an error if you pass in gibberish
   * @param date the date to convert
   * @param zone timezone of the date
   * @private
   */
  public static toDateTime(
    date: Date | DateTime | string | number,
    zone: string | Zone = TimeZone.EUROPE_BERLIN,
  ): DateTime {
    let dateTime: DateTime;
    if (date instanceof Date) dateTime = DateTime.fromJSDate(date);
    else if (date instanceof DateTime) dateTime = date;
    else if (typeof date === 'number') dateTime = DateTime.fromMillis(date);
    else dateTime = DateTime.fromISO(date);

    if (zone) return dateTime.setZone(zone);
    return dateTime;
  }

  /**
   * Returns an array of DateTime objects set to the beginning of each day between a start and an end date.
   * @param fromDate the start of the period
   * @param toDate the end of the period
   */
  public static getDaysBetween(
    fromDate: Date | DateTime | string | number,
    toDate?: Date | DateTime | string | number,
  ): DateTime[] {
    const from = DateUtils.toDateTime(fromDate).startOf('day');
    const to = DateUtils.toDateTime(toDate ?? new Date()).startOf('day');

    if (to < from) {
      return [];
    } else if (DateUtils.isSameDay(from, to)) {
      return [from];
    }

    const days = [];
    let currentDay = from;
    while (currentDay <= to) {
      days.push(currentDay);
      currentDay = currentDay.plus({ day: 1 });
    }
    return days;
  }
}
